import type {
  InvolvementLevel,
  InvolvementLevelBlockRow,
  PersonaToneBlockRow,
  PersonaToneEntry,
} from '@matter_extractor/shared';
import { INVOLVEMENT_LEVELS_MIN } from '@matter_extractor/shared';

/**
 * What Réglages > Personas edits BEFORE it writes (§11.5, planche
 * `ReglagesPersonas`).
 *
 * Both sections save « d'un bloc », with their own button - the mockup fixes
 * that and says why on the screen itself: neither list is versioned, so the
 * save OVERWRITES and there is no history to walk back.
 *
 * Since issue #45 the block IS the request: one `PUT` per section carries the
 * whole list, and the server applies it in one transaction. What used to live
 * here was the DIFF that a replay of per-row writes needed - the rewrites, the
 * creations, then the order once the creations had their identifiers - and it
 * is gone with the replay. What is left is the shape of that one payload, and
 * the two questions the screen answers on its own.
 *
 * Pure, so it can be tested without a base and without a render, and so the
 * route stays the thin wiring `vitest.config.ts` deliberately leaves out of the
 * coverage report.
 *
 * The server remains the authority on every rule below. What these functions
 * buy is the button greying itself out BEFORE a round trip, on the two things
 * an operator gets wrong by accident: an empty field and a duplicate name.
 */

/**
 * The server's name rule, mirrored - « insensible à la casse et aux espaces de
 * bord » (§4), and nothing more.
 *
 * A second spelling of a rule is a rule that drifts, so this one exists for ONE
 * purpose and says so: telling the operator about a collision inside the block
 * they are editing, which the server cannot refuse until the block is sent.
 * A collision with a row somebody ELSE added still comes back as a 409, and
 * `persona-labels.ts` words it.
 */
function nameKey(name: string): string {
  return name.trim().toLowerCase();
}

/* ------------------------------------------------- the involvement scale */

/** One editable cran, in the page, whether or not the base has it yet. */
export interface ScaleRow {
  /**
   * Stable for the life of the row, INCLUDING before it has an id.
   *
   * React needs a key for a list whose rows move, and a row that has never been
   * saved has no identifier to offer. Keying on the index would make a
   * reordering remount every field below the move and drop the caret.
   */
  readonly key: string;
  /** `null` while the row has never been saved: it is a creation. */
  readonly id: string | null;
  readonly name: string;
  readonly direction: string;
  /** How many personas point at it. Always zero for a row not yet in the base. */
  readonly personaCount: number;
}

/** The scale as the base holds it, ready to edit. */
export function scaleRowsOf(levels: readonly InvolvementLevel[]): ScaleRow[] {
  return levels.map((level) => ({
    key: level.id,
    id: level.id,
    name: level.name,
    direction: level.direction,
    personaCount: level.personaCount,
  }));
}

/** An empty cran, appended by « Ajouter un cran ». `seq` only has to be unique. */
export function newScaleRow(seq: number): ScaleRow {
  return { key: `nouveau-cran-${seq}`, id: null, name: '', direction: '', personaCount: 0 };
}

/**
 * The block the save SENDS: the whole section, in the order it is shown.
 *
 * The array is the order (§11.5), a row with an `id` is a rewrite and one
 * without is a creation - so the payload is the rows, trimmed, and nothing
 * else. A creation drops the key rather than sending `id: null`: absent is
 * what the contract reads as « create this one », and it is also what a row
 * the operator just typed actually carries.
 */
export function scaleBlockOf(rows: readonly ScaleRow[]): InvolvementLevelBlockRow[] {
  return rows.map((row) => {
    const written = { name: row.name.trim(), direction: row.direction.trim() };
    return row.id === null ? written : { id: row.id, ...written };
  });
}

/**
 * Whether the block differs from the base at all - what greys « Enregistrer ».
 *
 * Compared POSITION BY POSITION against what the base holds, which catches the
 * three things one gesture can change here at once: a text, an addition, and
 * the order. Position is CONTENT for a cran - it is the order the slider walks
 * - which is exactly what makes this right here and wrong for the tone library
 * below. Before issue #45 this had to be read off a diff, because the diff was
 * also what the save replayed; the block being the payload now, what is left is
 * the one question the button asks.
 */
export function scaleDirty(
  rows: readonly ScaleRow[],
  levels: readonly InvolvementLevel[],
): boolean {
  const asked = scaleBlockOf(rows);
  if (asked.length !== levels.length) return true;
  return asked.some((row, index) => {
    const stored = levels[index];
    return (
      stored === undefined ||
      stored.id !== row.id ||
      stored.name !== row.name ||
      stored.direction !== row.direction
    );
  });
}

/**
 * Why the block cannot be saved, in French, or `null` when it can.
 *
 * A sentence rather than a boolean: a button that greys itself without saying
 * why is a button an operator clicks three times before reading the form.
 */
export function scaleBlocked(rows: readonly ScaleRow[]): string | null {
  if (rows.length < INVOLVEMENT_LEVELS_MIN) {
    return 'L’échelle garde au moins deux crans : un slider à une seule position ne règle rien.';
  }
  if (rows.some((row) => row.name.trim() === '' || row.direction.trim() === '')) {
    return 'Chaque cran porte un nom et une direction d’IA.';
  }
  const twice = duplicateName(rows.map((row) => row.name));
  return twice === null ? null : `Deux crans portent le nom « ${twice} ». Les noms sont uniques.`;
}

/* --------------------------------------------------- the tone library */

/** One editable tone, in the page. Same shape as a cran, minus the count (§10.1). */
export interface ToneRow {
  readonly key: string;
  readonly id: string | null;
  readonly name: string;
  readonly text: string;
}

export function toneRowsOf(tones: readonly PersonaToneEntry[]): ToneRow[] {
  return tones.map((tone) => ({ key: tone.id, id: tone.id, name: tone.name, text: tone.text }));
}

export function newToneRow(seq: number): ToneRow {
  return { key: `nouveau-ton-${seq}`, id: null, name: '', text: '' };
}

/** The block the save sends. No order to carry: a cloud of tags has no first element. */
export function toneBlockOf(rows: readonly ToneRow[]): PersonaToneBlockRow[] {
  return rows.map((row) => {
    const written = { name: row.name.trim(), text: row.text.trim() };
    return row.id === null ? written : { id: row.id, ...written };
  });
}

/**
 * Whether the library differs from the base - and compared BY IDENTIFIER,
 * where the scale above is compared by position.
 *
 * The asymmetry is the one difference between the two sections: a cran's
 * position is content, a tone's is not (§11.5 - a cloud of tags has no first
 * element), and the library comes back sorted by the server whatever order it
 * was sent in. Compared position by position, a re-read that hands the same
 * tones back in another order would say « modifié » about a section nobody
 * touched - and the reachable path is not hypothetical: the persona editor and
 * this screen share the `['persona-tones']` cache, and the editor writes its
 * own freshly created tone into it (`persona-form.tsx`) with a French collation
 * where the server sorts on the normalized name. The button would arm on its
 * own, and pressing it would send rows read before the refetch.
 */
export function toneDirty(rows: readonly ToneRow[], tones: readonly PersonaToneEntry[]): boolean {
  const asked = toneBlockOf(rows);
  if (asked.length !== tones.length) return true;
  const byId = new Map(tones.map((tone) => [tone.id, tone]));
  return asked.some((row) => {
    if (row.id === undefined) return true;
    const stored = byId.get(row.id);
    return stored === undefined || stored.name !== row.name || stored.text !== row.text;
  });
}

/** Why the library cannot be saved, or `null`. No minimum: an empty library is legal. */
export function toneBlocked(rows: readonly ToneRow[]): string | null {
  if (rows.some((row) => row.name.trim() === '' || row.text.trim() === '')) {
    return 'Chaque ton porte un nom et un texte.';
  }
  const twice = duplicateName(rows.map((row) => row.name));
  return twice === null ? null : `Deux tons portent le nom « ${twice} ». Les noms sont uniques.`;
}

/* -------------------------------------------------------------- shared */

/** The FIRST name that appears twice, as the operator spelled it, or `null`. */
function duplicateName(names: readonly string[]): string | null {
  const seen = new Set<string>();
  for (const name of names) {
    const key = nameKey(name);
    if (key === '') continue;
    if (seen.has(key)) return name.trim();
    seen.add(key);
  }
  return null;
}

/**
 * Moves one row up or down, and hands back the list unchanged when the move
 * would fall off the end.
 *
 * A COPY either way, never the same reference: the caller is a `setRows`, which
 * wants a new array, and a function that sometimes returns its own argument is
 * a function whose callers eventually branch on identity. What keeps the no-op
 * from ever being asked for is the screen: the first row's « monter » and the
 * last row's « descendre » are disabled, by the same bound this checks.
 */
export function moveRow<Row>(rows: readonly Row[], index: number, delta: -1 | 1): Row[] {
  const target = index + delta;
  if (index < 0 || index >= rows.length || target < 0 || target >= rows.length) {
    return [...rows];
  }
  const next = [...rows];
  const moved = next[index];
  const displaced = next[target];
  // Narrowed rather than asserted: `noUncheckedIndexedAccess` makes both reads
  // `Row | undefined`, and a non-null assertion is refused in this codebase.
  if (moved === undefined || displaced === undefined) return next;
  next[index] = displaced;
  next[target] = moved;
  return next;
}
